import { useState, useEffect } from "react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import MapPinPicker from "@/components/MapPinPicker";
import { Home, MapPin, Mail } from "lucide-react";
import { toast } from "sonner";

export default function HomePinDialog({ open, onOpenChange }) {
    const { user, setUser } = useAuth();
    const [addr, setAddr] = useState("");
    const [lat, setLat] = useState(null);
    const [lng, setLng] = useState(null);
    const [radius, setRadius] = useState(5);
    const [digestOn, setDigestOn] = useState(true);
    const [busy, setBusy] = useState(false);

    // Sync from user whenever the dialog opens or the user object changes
    useEffect(() => {
        if (!open) return;
        setAddr(user?.home_address || "");
        setLat(user?.home_lat ?? null);
        setLng(user?.home_lng ?? null);
        setRadius(user?.filter_radius_km || 5);
        // notify_weekend_digest defaults to true if not set
        setDigestOn(user?.notify_weekend_digest !== false && !user?.digest_unsubscribed);
    }, [open, user?.home_address, user?.home_lat, user?.home_lng, user?.filter_radius_km,
        user?.notify_weekend_digest, user?.digest_unsubscribed]);

    const hasSavedPin = !!(user?.home_lat && user?.home_lng);

    const save = async () => {
        setBusy(true);
        try {
            // Best-effort timezone detection so digests hit at their real Friday 9am, not UTC 9am.
            const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
            const { data } = await api.patch("/auth/me/preferences", {
                home_lat: lat, home_lng: lng, home_address: addr, filter_radius_km: radius,
                notify_weekend_digest: digestOn, timezone: tz,
            });
            setUser(data);
            toast.success("Home pin saved");
            onOpenChange(false);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    const clear = async () => {
        setBusy(true);
        try {
            const { data } = await api.patch("/auth/me/preferences", { clear_home: true });
            setUser(data);
            setAddr(""); setLat(null); setLng(null); setRadius(5);
            toast.success("Home pin cleared");
            onOpenChange(false);
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-md">
                <DialogHeader>
                    <DialogTitle className="font-display flex items-center gap-2">
                        <Home size={18} /> {hasSavedPin ? "Update your home pin" : "Set your home pin"}
                    </DialogTitle>
                </DialogHeader>
                <p className="text-sm text-muted-foreground">
                    We&apos;ll use this to show what&apos;s within reach on the map. Update anytime.
                </p>
                {hasSavedPin && lat && lng && (
                    <div className="text-xs bg-secondary rounded-lg p-2 flex items-center gap-1.5">
                        <MapPin size={11} className="text-primary" />
                        <span className="font-semibold">Current:</span>
                        <span className="text-muted-foreground truncate">{user.home_address || `${lat.toFixed(4)}, ${lng.toFixed(4)}`}</span>
                    </div>
                )}
                <AddressAutocomplete
                    label="Home location"
                    value={addr}
                    onChange={setAddr}
                    onPick={(s) => { setAddr(s.display_name); setLat(s.lat); setLng(s.lng); }}
                />
                <MapPinPicker
                    lat={Number.isFinite(lat) ? lat : NaN}
                    lng={Number.isFinite(lng) ? lng : NaN}
                    height={200}
                    onChange={async ({ lat: la, lng: ln }) => {
                        setLat(la); setLng(ln);
                        try {
                            const { data } = await api.get(`/reverse-geocode?lat=${la}&lng=${ln}`);
                            setAddr(data?.display_name || "Pinned on the map");
                        } catch { setAddr("Pinned on the map"); }
                    }}
                />
                <p className="text-xs text-muted-foreground" data-testid="home-privacy-note">
                    Only you see this — it just centres your map and search radius.
                </p>
                <div className="mt-2">
                    <div className="flex items-center justify-between">
                        <Label>Search radius</Label>
                        <span className="text-sm font-display font-black" data-testid="radius-value">{radius} km</span>
                    </div>
                    <Slider
                        data-testid="radius-slider"
                        min={1} max={50} step={1}
                        value={[radius]}
                        onValueChange={([v]) => setRadius(v)}
                        className="mt-3"
                    />
                </div>

                {/* Weekend digest opt-in. Toggling this here lands the preference straight
                    into the user doc; the Friday-9am scheduler reads it on its next tick. */}
                <label className="mt-4 flex items-start gap-3 p-3 rounded-xl border border-stone-200 bg-secondary/30 cursor-pointer hover:bg-secondary/50 transition-colors">
                    <input
                        type="checkbox"
                        checked={digestOn}
                        onChange={(e) => setDigestOn(e.target.checked)}
                        data-testid="digest-toggle"
                        className="mt-0.5 w-4 h-4 rounded accent-primary"
                    />
                    <div className="flex-1">
                        <div className="text-sm font-semibold flex items-center gap-1.5">
                            <Mail size={12} /> Weekend digest email
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                            A short Friday-morning email with what&apos;s on near you this weekend. Unsubscribe any time.
                        </div>
                    </div>
                </label>
                <DialogFooter className="flex-row gap-2 sm:justify-between">
                    {hasSavedPin && (
                        <Button variant="ghost" onClick={clear} disabled={busy} data-testid="home-pin-clear" className="rounded-full">
                            Clear
                        </Button>
                    )}
                    <Button onClick={save} disabled={busy || !lat} data-testid="home-pin-save" className="rounded-full bg-primary hover:bg-primary/90 hover:text-white ml-auto">
                        {busy ? "Saving…" : hasSavedPin ? "Update home pin" : "Save home pin"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
